import { useEffect, useMemo, useState } from "react";
import {
  fetchVideoResourceMappings,
  fetchVideos,
  indexVideoResourceMappings,
  type Video,
  type VideoResourceMapping,
} from "@/lib/cms";
import VideoPlayerModal from "@/components/cms/VideoPlayerModal";

const FETCH_COUNT = 500;
const DESKTOP_INITIAL = 20;
const DESKTOP_BATCH = 20;
const MOBILE_INITIAL = 6;
const MOBILE_BATCH = 6;

function formatRelativePublished(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 45) return "just now";
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, "minute");
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return rtf.format(-diffHour, "hour");
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return rtf.format(-diffDay, "day");
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return rtf.format(-diffWeek, "week");
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return rtf.format(-diffMonth, "month");
  const diffYear = Math.floor(diffDay / 365);
  return rtf.format(-diffYear, "year");
}

export default function VideoLibraryGrid() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [mappings, setMappings] = useState<VideoResourceMapping[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadMoreClicks, setLoadMoreClicks] = useState(0);
  const [isNarrow, setIsNarrow] = useState(false);
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetchVideos({ limit: FETCH_COUNT, sort: "-publishedAt" }),
      fetchVideoResourceMappings().catch(() => ({ docs: [] as VideoResourceMapping[] })),
    ])
      .then(([videosRes, mappingsRes]) => {
        if (cancelled) return;
        setVideos(videosRes.docs);
        setMappings(mappingsRes.docs);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[VideoLibraryGrid] fetch failed", err);
        setStatus("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to load videos",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const mappingIndex = useMemo(
    () => indexVideoResourceMappings(mappings),
    [mappings],
  );

  const base = isNarrow ? MOBILE_INITIAL : DESKTOP_INITIAL;
  const step = isNarrow ? MOBILE_BATCH : DESKTOP_BATCH;
  const visibleCount =
    status === "ready" && videos.length > 0
      ? Math.min(base + loadMoreClicks * step, videos.length)
      : 0;

  function loadMore() {
    setLoadMoreClicks((c) => c + 1);
  }

  const slice = videos.slice(0, visibleCount);
  const canLoadMore = visibleCount < videos.length;

  return (
    <section
      className="w-full bg-black py-12 font-fira text-white sm:py-16"
      aria-labelledby="video-library-heading"
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <header className="mb-8 text-left md:mb-10">
          <p className="font-fira mb-3 text-xs font-normal uppercase tracking-[0.18em] text-[#FFFFFF] md:text-sm">
            Library
          </p>
          <h2
            id="video-library-heading"
            className="font-fira-condensed text-[30px] font-medium md:font-semibold leading-tight text-white md:text-[42px]"
          >
            Find what fits your week
          </h2>
        </header>

        {status === "error" && errorMessage ? (
          <p className="mb-6 text-[14px] leading-relaxed text-red-300/90">
            {errorMessage}
          </p>
        ) : null}

        {status === "loading" && (
          <p className="text-[14px] text-[#FFFFFF85]">Loading videos…</p>
        )}

        {slice.length > 0 && (
          <>
            <div
              className="grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-4"
              role="list"
            >
              {slice.map((video) => {
                const publishedRel = formatRelativePublished(video.publishedAt);
                return (
                  <button
                    key={video.id}
                    type="button"
                    onClick={() => setActiveVideo(video)}
                    role="listitem"
                    aria-label={`Open: ${video.title}`}
                    className="group flex flex-col text-left"
                  >
                    <div className="relative aspect-video w-full shrink-0 overflow-hidden rounded-[12px] border border-[#FFFFFF52]">
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt=""
                          className="h-full w-full object-cover transition group-hover:opacity-95"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : null}
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center md:hidden">
                        <img
                          src="/resources/rplay.svg"
                          alt=""
                          width={40}
                          height={40}
                          className="h-10 w-10 opacity-90 drop-shadow-md"
                        />
                      </span>
                      {video.durationFormatted ? (
                        <span className="absolute bottom-2 right-2 rounded bg-black/85 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white md:bottom-3 md:right-3">
                          {video.durationFormatted}
                        </span>
                      ) : null}
                    </div>
                    <div className="font-fira mt-3 flex min-h-0 flex-1 flex-col pb-3 text-left md:mt-4">
                      <p className="line-clamp-2 text-[18px] font-normal leading-snug text-[#FFFFFF] md:text-base">
                        {video.title}
                      </p>
                      <p className="mt-2 font-normal text-[#CFCFCFE5] text-xs">
                        {video.viewCountFormatted ?? ""}
                        {publishedRel ? ` • ${publishedRel}` : ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {canLoadMore ? (
              <div className="mt-10 flex justify-center md:mt-12">
                <button
                  type="button"
                  onClick={loadMore}
                  className="w-full max-w-md rounded-lg border border-white/30 bg-transparent px-6 py-3.5 text-center text-sm font-medium text-white transition hover:bg-white/5 md:w-auto md:min-w-[200px] md:px-10"
                >
                  Load more
                </button>
              </div>
            ) : null}
          </>
        )}

        {status === "ready" && videos.length === 0 && (
          <p className="text-[14px] text-[#FFFFFF85]">No videos yet.</p>
        )}
      </div>

      {activeVideo ? (
        <VideoPlayerModal
          video={activeVideo}
          mapping={mappingIndex.get(activeVideo.youtubeVideoId) ?? null}
          onClose={() => setActiveVideo(null)}
        />
      ) : null}
    </section>
  );
}